/**
 * Web Push subscription helpers (BL-205).
 *
 * Browser-only. Registers a service worker, subscribes the browser to Web Push
 * with the app's VAPID public key, and registers the resulting subscription
 * (endpoint + p256dh/auth keys) with platform-api so the push processor can
 * deliver to it. All calls are safe no-ops when the browser lacks support or
 * the VAPID key is absent (the feature stays dark without a keypair).
 */

import type { TonnieClient } from './client/base';
import { createNotificationsService } from './services/notifications.service';

/** True when this browser can do Web Push at all. */
export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** VAPID public key (base64url) → the Uint8Array the PushManager expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function keyToBase64(sub: PushSubscription, name: 'p256dh' | 'auth'): string {
  const key = sub.getKey(name);
  if (!key) return '';
  return btoa(String.fromCharCode(...new Uint8Array(key)));
}

export interface SubscribeWebPushOptions {
  client: TonnieClient;
  /** VAPID public key (must match the server's VAPID_PUBLIC_KEY). */
  vapidPublicKey: string;
  /** Service-worker script path served at the app origin (default `/sw.js`). */
  swPath?: string;
}

export type SubscribeWebPushResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'no-vapid-key' | 'permission-denied' | 'error' };

/**
 * Ensure the browser is subscribed to Web Push and the subscription is
 * registered server-side. Idempotent: reuses an existing subscription. Prompts
 * for notification permission if not already decided — call it from a user
 * gesture (e.g. an opt-in toggle), not on page load.
 */
export async function subscribeWebPush(
  opts: SubscribeWebPushOptions
): Promise<SubscribeWebPushResult> {
  if (!isWebPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!opts.vapidPublicKey) return { ok: false, reason: 'no-vapid-key' };

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'permission-denied' };

    const registration = await navigator.serviceWorker.register(opts.swPath ?? '/sw.js');
    await navigator.serviceWorker.ready;

    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(opts.vapidPublicKey),
      }));

    await createNotificationsService(opts.client).registerDeviceToken({
      provider: 'webpush',
      token: subscription.endpoint,
      keys: {
        p256dh: keyToBase64(subscription, 'p256dh'),
        auth: keyToBase64(subscription, 'auth'),
      },
      platform: 'web',
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/** Unsubscribe this browser and unregister the subscription server-side. */
export async function unsubscribeWebPush(client: TonnieClient): Promise<void> {
  if (!isWebPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await createNotificationsService(client).unregisterDeviceToken(endpoint);
  } catch {
    /* best-effort */
  }
}
